import type { PointOptionsObject } from "highcharts";
import React from "react";
import { INVALID_COLOR } from "../series/getMeasureSeries";
import { MeasurePointMetadata } from "../PointMetadata";
import type { SelectedQuantity } from "../../useSelectedQuantities";
import { quantityColor } from "../../useSelectedQuantities";
import Marker from "@ui/Highcharts/ui/Marker";
import testSerieData from "@ui/Highcharts/lib/testSerieData";
import { color } from "@ui";
import t from "../../../../i18n";
import type { Period } from "@ui/Highcharts/hooks/usePeriod";
import {
  DiamondMarker,
  PartialValidCircleMarker,
  PointSymbol,
} from "@ui/Highcharts/ui/PointSymbol";
import type { NonEmptyArray } from "diagnostics-lib/array";
import { styled } from "@linaria/react";
import type { MeasuresChartType } from "../../Measures/MeasuresChartType";
import Highcharts from "highcharts";
import { useAfterSetExtremesY } from "@ui/Highcharts/hooks/useAfterSetExtremes";

const MinMax = styled.div<{ color: ReturnType<typeof quantityColor> | string }>`
  border-top: 1px dashed ${props => props.color};
  height: 1px;
  width: 15px;
`;

const AnalysisFilter = styled.div<{
  color: ReturnType<typeof quantityColor> | string;
  withBackground: boolean;
}>`
  height: 15px;
  width: 15px;
  position: relative;
  background-color: ${props => (props.withBackground ? color.phosphorus50 : "transparent")};

  &::before,
  &::after {
    border-top: 1px dashed ${props => props.color};
    display: block;
    content: "";
    width: 7px;
    height: 1px;
    position: absolute;
    top: 50%;
  }

  &::before {
    left: 0;
    transform: rotateZ(-45deg);
  }

  &::after {
    right: 0;
    transform: rotateZ(45deg);
  }
`;

function hasNoValidation(p: PointOptionsObject) {
  const metadata = p.custom as MeasurePointMetadata;
  return p.y !== null && metadata.validity === "NO_VALIDATION";
}

function hasInvalidAuto(p: PointOptionsObject) {
  const metadata = p.custom as MeasurePointMetadata;
  return p.y !== null && metadata.validity === "INVALID_AUTO";
}
function hasInvalidAutoCrossFilter(p: PointOptionsObject) {
  const metadata = p.custom as MeasurePointMetadata;
  return p.y !== null && metadata.validity === "INVALID_AUTO_CROSS_FILTER";
}
function hasInvalidManual(p: PointOptionsObject) {
  const metadata = p.custom as MeasurePointMetadata;
  return p.y !== null && metadata.validity === "INVALID_MANUAL";
}

function hasValidAuto(p: PointOptionsObject) {
  const metadata = p.custom as MeasurePointMetadata;
  return p.y !== null && metadata.validity === "VALID_AUTO";
}

function hasValidPartialInputs(p: PointOptionsObject) {
  const metadata = p.custom as MeasurePointMetadata;
  return p.y !== null && metadata.validity === "VALID_PARTIAL_INPUTS";
}

function hasValidManual(p: PointOptionsObject) {
  const metadata = p.custom as MeasurePointMetadata;
  return p.y !== null && metadata.validity === "VALID_MANUAL";
}
function hasInheritedInvalid(p: PointOptionsObject) {
  const metadata = p.custom as MeasurePointMetadata;
  return p.y !== null && metadata.validity === "INVALID_INHERITED";
}

interface LegendOptions {
  noValidation: boolean;
  valid_auto: boolean;
  valid_manual: boolean;
  valid_partial_inputs: boolean;
  invalid_auto: boolean;
  invalid_auto_cross_filter: boolean;
  invalid_manual: boolean;
  inheritedInvalid: boolean;
}

interface P {
  chartRef: React.RefObject<Highcharts.Chart>;
  period: Period;
  selectedQuantities: NonEmptyArray<SelectedQuantity>;
  series: (
    | Highcharts.SeriesLineOptions
    | Highcharts.SeriesFlagsOptions
    | Highcharts.SeriesArearangeOptions
    | Highcharts.SeriesScatterOptions
  )[];
  selectedChartOption?: MeasuresChartType;
  displayInvalidPoints: boolean;
}

function MeasuresLegend({
  chartRef,
  series,
  period,
  selectedQuantities,
  selectedChartOption,
  displayInvalidPoints,
}: P) {
  const legendOptions = React.useMemo(
    () =>
      selectedQuantities.reduce<LegendOptions>(
        (acc, selectedQuantity: SelectedQuantity) => {
          const serie = series.find(s => s.name === selectedQuantity.quantity.name);

          if (!serie) return acc;

          return {
            noValidation: acc.noValidation || testSerieData(serie, period, hasNoValidation),
            valid_auto: acc.valid_auto || testSerieData(serie, period, hasValidAuto),
            valid_manual: acc.valid_manual || testSerieData(serie, period, hasValidManual),
            valid_partial_inputs:
              acc.valid_partial_inputs || testSerieData(serie, period, hasValidPartialInputs),
            invalid_auto: acc.invalid_auto || testSerieData(serie, period, hasInvalidAuto),
            invalid_auto_cross_filter:
              acc.invalid_auto_cross_filter ||
              testSerieData(serie, period, hasInvalidAutoCrossFilter),
            invalid_manual: acc.invalid_manual || testSerieData(serie, period, hasInvalidManual),
            inheritedInvalid:
              acc.inheritedInvalid || testSerieData(serie, period, hasInheritedInvalid),
          };
        },
        {
          noValidation: false,
          valid_auto: false,
          valid_manual: false,
          valid_partial_inputs: false,
          invalid_auto: false,
          invalid_auto_cross_filter: false,
          invalid_manual: false,
          inheritedInvalid: false,
        },
      ),
    [period, selectedQuantities, series],
  );

  const measureColor = React.useMemo(
    () =>
      selectedQuantities.length > 1
        ? color.raw.titanium600
        : quantityColor(selectedQuantities[0].colorIndex),
    [selectedQuantities],
  );

  const [hasMetrologyPlotLines, toggleMetrologyPlotLines] = React.useState(false);
  const [hasMetrologyPlotBands, toggleMetrologyPlotBands] = React.useState(false);

  //  #region comparePlotLinesOrBandsToChartZoom
  /**
   * @description
   * Following hooks (inside current region) are used to determine if the chart has metrology
   * plotLines or plotBands and if it fits in the current zoom to adjust legend accordingly.
   *
   * We can't use data values associated with chart
   * points as they are static (e.g don't depend on chart zoom state).
   */
  const comparePlotLinesOrBandsToChart = React.useCallback(
    /**
     * @note Highchart event handler expect Highcharts.SVGElement as target
     * but passes Highcharts.Axis instead.
     */
    (target: unknown, key: "value" | "from", min: number, max: number) =>
      (target instanceof Highcharts.Axis &&
        target.plotLinesAndBands?.some(
          p =>
            p instanceof Highcharts.PlotLineOrBand &&
            typeof p.options[key] === "number" &&
            p.options[key] >= min &&
            typeof p.options[key] === "number" &&
            p.options[key] <= max,
        )) ||
      false,
    [],
  );

  const onAfterSetExtremesY = React.useCallback(
    (e: Highcharts.AxisSetExtremesEventObject) => {
      toggleMetrologyPlotLines(comparePlotLinesOrBandsToChart(e.target, "value", e.min, e.max));
      toggleMetrologyPlotBands(comparePlotLinesOrBandsToChart(e.target, "from", e.min, e.max));
    },
    [comparePlotLinesOrBandsToChart],
  );

  useAfterSetExtremesY(chartRef, onAfterSetExtremesY, {
    onLayoutEffect: axes => {
      toggleMetrologyPlotLines(
        axes.some(y =>
          y.min && y.max ? comparePlotLinesOrBandsToChart(y, "value", y.min, y.max) : false,
        ),
      );
      toggleMetrologyPlotBands(
        axes.some(y =>
          y.min && y.max ? comparePlotLinesOrBandsToChart(y, "from", y.min, y.max) : false,
        ),
      );
    },
    yAxesFilter: y =>
      !y.userOptions.id?.includes("flagAxis") && !y.userOptions.id?.includes("navigator"),
  });
  //  #endregion comparePlotLinesOrBandsToChartZoom
  return (
    <>
      {/* 
        Validity
        =======================================================================
      */}
      {displayInvalidPoints ? (
        <>
          {legendOptions.noValidation && (
            <Marker name={t("measures.chart.legend.no_validation.name")}>
              <PointSymbol height={9} width={9} color={measureColor} />
            </Marker>
          )}
          {legendOptions.valid_auto && (
            <Marker
              name={t("measures.chart.legend.valid_auto.name")}
              description={t("measures.chart.legend.modal.valid_auto.description")}
            >
              <PointSymbol height={9} width={9} color={measureColor} fill={measureColor} />
            </Marker>
          )}
          {legendOptions.valid_manual && (
            <Marker
              name={t("measures.chart.legend.valid_manual.name")}
              description={t("measures.chart.legend.modal.valid_manual.description")}
            >
              <DiamondMarker height={9} width={9} color={measureColor} fill={measureColor} />
            </Marker>
          )}
          {(legendOptions.invalid_auto ||
            legendOptions.invalid_auto_cross_filter ||
            legendOptions.inheritedInvalid) && (
            <Marker
              name={t("measures.chart.legend.invalid_auto.name")}
              description={t("measures.chart.legend.modal.invalid_auto.description")}
            >
              <PointSymbol height={9} width={9} color={INVALID_COLOR} fill={INVALID_COLOR} />
            </Marker>
          )}
          {legendOptions.invalid_manual && (
            <Marker
              name={t("measures.chart.legend.invalid_manual.name")}
              description={t("measures.chart.legend.modal.invalid_manual.description")}
            >
              <DiamondMarker height={9} width={9} color={INVALID_COLOR} fill={INVALID_COLOR} />
            </Marker>
          )}
        </>
      ) : (
        (legendOptions.noValidation ||
          legendOptions.valid_auto ||
          legendOptions.valid_manual ||
          legendOptions.inheritedInvalid ||
          legendOptions.invalid_auto ||
          legendOptions.invalid_manual) && (
          <Marker
            name={t("symptoms.chart.legend.measured.name")}
            description={t("symptoms.chart.legend.measured.description")}
          >
            <PointSymbol height={9} width={9} color={measureColor} fill={measureColor} />
          </Marker>
        )
      )}

      {legendOptions.valid_partial_inputs && (
        <Marker
          name={t("symptoms.chart.legend.valid_partial_inputs")}
          description={t("symptoms.chart.legend.valid_partial_inputs")}
        >
          <PartialValidCircleMarker height={9} width={9} color={measureColor} />
        </Marker>
      )}

      {/* 
        Metrology Legend 
        =======================================================================
      */}
      {hasMetrologyPlotLines && (
        <Marker
          name={
            selectedChartOption === "value"
              ? t("measures.chart.legend.value.minmax.title")
              : t("measures.chart.legend.std.max.title")
          }
          description={
            selectedChartOption === "value"
              ? t("measures.chart.legend.value.minmax.description")
              : t("measures.chart.legend.std.max.description")
          }
        >
          <MinMax color={measureColor} />
        </Marker>
      )}
      {hasMetrologyPlotBands && (
        <Marker
          name={t("measures.chart.legend.analysisFilter.title")}
          description={t("measures.chart.legend.analysisFilter.description")}
        >
          <AnalysisFilter color={measureColor} withBackground={selectedQuantities.length === 1} />
        </Marker>
      )}
    </>
  );
}

export default React.memo(MeasuresLegend);
